import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const Services: React.FC = () => {
  const services = [
    {
      id: 'website-dev',
      title: 'Custom Website Development',
      icon: '🌐',
      description: 'Professional business websites, portfolio sites, and landing pages built with modern technologies.',
      features: [
        'Responsive Design',
        'SEO Optimization',
        'Fast Loading',
        'Mobile-First Approach',
        'Cross-Browser Compatibility'
      ],
      technologies: ['HTML5', 'CSS3', 'JavaScript', 'React', 'PHP']
    },
    {
      id: 'web-apps',
      title: 'Web Application Development',
      icon: '⚡',
      description: 'Scalable web applications with advanced functionality, user authentication, and real-time features.',
      features: [
        'User Authentication',
        'Database Integration',
        'API Development',
        'Real-time Features',
        'Admin Dashboards'
      ],
      technologies: ['Node.js', 'Express.js', 'MongoDB', 'React', 'Socket.io']
    },
    {
      id: 'ecommerce',
      title: 'E-Commerce Solutions',
      icon: '🛒',
      description: 'Complete online stores with payment integration, inventory management, and customer portals.',
      features: [
        'Payment Gateway Integration',
        'Inventory Management',
        'Order Processing',
        'Customer Accounts',
        'Analytics Dashboard'
      ],
      technologies: ['WooCommerce', 'Stripe', 'PayPal', 'PHP', 'MySQL']
    },
    {
      id: 'frontend-dev',
      title: 'Frontend Development',
      icon: '🎨',
      description: 'Modern, interactive user interfaces with smooth animations and exceptional user experience.',
      features: [
        'Interactive UI/UX',
        'Smooth Animations',
        'Component Architecture',
        'Performance Optimization',
        'Accessibility Compliance'
      ],
      technologies: ['React', 'TypeScript', 'Tailwind CSS', 'Framer Motion', 'Next.js']
    },
    {
      id: 'backend-dev',
      title: 'Backend Development',
      icon: '⚙️',
      description: 'Robust server-side solutions with secure APIs, database management, and scalable architecture.',
      features: [
        'RESTful APIs',
        'Database Design',
        'Authentication Systems',
        'Security Implementation',
        'Performance Optimization'
      ],
      technologies: ['Node.js', 'Express.js', 'MongoDB', 'PostgreSQL', 'Redis']
    },
    {
      id: 'api-integration',
      title: 'API Integration',
      icon: '🔗',
      description: 'Seamless integration with third-party services, payment gateways, and external APIs.',
      features: [
        'Payment Gateways',
        'Social Media APIs',
        'Cloud Services',
        'Third-party Integrations',
        'Webhook Implementation'
      ],
      technologies: ['Stripe', 'PayPal', 'AWS', 'Google APIs', 'Twilio']
    },
    {
      id: 'cms-dev',
      title: 'CMS Development',
      icon: '📝',
      description: 'Custom content management systems and WordPress solutions for easy content updates.',
      features: [
        'Custom Admin Panels',
        'Content Management',
        'User Role Management',
        'Media Management',
        'SEO-Friendly URLs'
      ],
      technologies: ['WordPress', 'PHP', 'MySQL', 'Custom CMS', 'AdminLTE']
    },
    {
      id: 'optimization',
      title: 'Performance Optimization',
      icon: '🚀',
      description: 'Website speed optimization, SEO improvements, and performance monitoring services.',
      features: [
        'Page Speed Optimization',
        'SEO Improvements',
        'Image Optimization',
        'Caching Implementation',
        'Performance Monitoring'
      ],
      technologies: ['Google PageSpeed', 'GTmetrix', 'SEO Tools', 'CDN', 'Web Vitals']
    },
    {
      id: 'maintenance',
      title: 'Maintenance & Support',
      icon: '🛠️',
      description: 'Ongoing website maintenance, updates, security patches, and technical support.',
      features: [
        'Security Updates',
        'Bug Fixes',
        'Content Updates',
        'Performance Monitoring',
        '24/7 Support'
      ],
      technologies: ['Monitoring Tools', 'Backup Systems', 'Security Tools', 'Analytics', 'Support Desk']
    }
  ];

  const industries = [
    'Education & Schools',
    'Healthcare & Medical',
    'Retail & E-commerce',
    'Real Estate',
    'Hospitality & Tourism',
    'Manufacturing',
    'Professional Services',
    'Non-profit Organizations',
    'Startups',
    'Local Businesses'
  ];

  const processSteps = [
    {
      step: '01',
      title: 'Discovery & Planning',
      description: 'Understanding your requirements, goals, and target audience to create a comprehensive project roadmap.'
    },
    {
      step: '02',
      title: 'Design & Prototyping',
      description: 'Creating wireframes, mockups, and interactive prototypes to visualize the final product.'
    },
    {
      step: '03',
      title: 'Development',
      description: 'Building the solution using modern technologies and best coding practices.'
    },
    {
      step: '04',
      title: 'Testing & Quality Assurance',
      description: 'Rigorous testing for functionality, performance, security, and cross-browser compatibility.'
    },
    {
      step: '05',
      title: 'Deployment & Launch',
      description: 'Seamless deployment, final optimizations, and successful project launch.'
    },
    {
      step: '06',
      title: 'Support & Maintenance',
      description: 'Ongoing support, updates, and maintenance to ensure optimal performance.'
    }
  ];

  return (
    <div className="services-page">
      {/* Hero Section */}
      <section className="services-hero">
        <motion.div
          className="hero-content"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1>Our Services</h1>
          <p className="hero-subtitle">
            Comprehensive web development solutions tailored to transform your business digitally
          </p>
        </motion.div>
        <div className="hero-bg">
          <div className="floating-elements">
            <div className="floating-orb orb-1"></div>
            <div className="floating-orb orb-2"></div>
            <div className="floating-orb orb-3"></div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="services-grid-section">
        <div className="container">
          <motion.h2
            className="section-title"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            What We Offer
          </motion.h2>

          <div className="services-grid">
            {services.map((service, index) => (
              <motion.div
                key={service.id}
                className="service-card-detailed"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <div className="service-header">
                  <div className="service-icon">{service.icon}</div>
                  <h3>{service.title}</h3>
                </div>

                <p className="service-description">{service.description}</p>

                <div className="service-features">
                  <h4>Key Features:</h4>
                  <ul>
                    {service.features.map((feature, i) => (
                      <li key={i}>{feature}</li>
                    ))}
                  </ul>
                </div>

                <div className="service-tech">
                  <h4>Technologies:</h4>
                  <div className="tech-tags">
                    {service.technologies.map((tech, i) => (
                      <span key={i} className="tech-tag">{tech}</span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Technologies Section */}
      <section className="technologies-section">
        <motion.div
          className="container"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2>Technologies We Use</h2>
          <p className="section-subtitle">
            We leverage cutting-edge technologies to build robust, scalable, and future-ready solutions
          </p>

          <div className="tech-categories">
            <div className="tech-category">
              <h3>Frontend</h3>
              <div className="tech-icons">
                <span className="tech-item">React</span>
                <span className="tech-item">TypeScript</span>
                <span className="tech-item">Next.js</span>
                <span className="tech-item">Tailwind CSS</span>
                <span className="tech-item">Framer Motion</span>
              </div>
            </div>

            <div className="tech-category">
              <h3>Backend</h3>
              <div className="tech-icons">
                <span className="tech-item">Node.js</span>
                <span className="tech-item">Express.js</span>
                <span className="tech-item">PHP</span>
                <span className="tech-item">Python</span>
                <span className="tech-item">GraphQL</span>
              </div>
            </div>

            <div className="tech-category">
              <h3>Databases</h3>
              <div className="tech-icons">
                <span className="tech-item">MongoDB</span>
                <span className="tech-item">MySQL</span>
                <span className="tech-item">PostgreSQL</span>
                <span className="tech-item">Redis</span>
                <span className="tech-item">Firebase</span>
              </div>
            </div>

            <div className="tech-category">
              <h3>Tools & Platforms</h3>
              <div className="tech-icons">
                <span className="tech-item">Git</span>
                <span className="tech-item">Docker</span>
                <span className="tech-item">AWS</span>
                <span className="tech-item">Vercel</span>
                <span className="tech-item">WordPress</span>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Development Process */}
      <section className="process-section">
        <motion.div
          className="container"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2>Our Development Process</h2>
          <div className="process-timeline">
            {processSteps.map((step, index) => (
              <motion.div
                key={index}
                className="process-step-card"
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <div className="step-number">{step.step}</div>
                <div className="step-content">
                  <h4>{step.title}</h4>
                  <p>{step.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Industries Served */}
      <section className="industries-section">
        <motion.div
          className="container"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2>Industries We Serve</h2>
          <p className="section-subtitle">
            We have experience working with businesses across various industries
          </p>

          <div className="industries-grid">
            {industries.map((industry, index) => (
              <motion.div
                key={index}
                className="industry-item"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
              >
                <span className="industry-icon">🏢</span>
                <span>{industry}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* FAQ Preview */}
      <section className="faq-preview">
        <motion.div
          className="container"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2>Frequently Asked Questions</h2>
          <div className="faq-preview-grid">
            <div className="faq-preview-item">
              <h4>How long does a typical project take?</h4>
              <p>Project timelines vary from 2-12 weeks depending on complexity and scope.</p>
            </div>
            <div className="faq-preview-item">
              <h4>Do you provide ongoing support?</h4>
              <p>Yes, we offer comprehensive maintenance and support packages.</p>
            </div>
            <div className="faq-preview-item">
              <h4>What is your pricing structure?</h4>
              <p>We offer flexible pricing based on project requirements and scope.</p>
            </div>
          </div>
          <div className="faq-cta">
            <Link to="/faq" className="btn-secondary">View All FAQs</Link>
          </div>
        </motion.div>
      </section>

      {/* Final CTA */}
      <section className="services-cta">
        <motion.div
          className="container"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2>Ready to Start Your Project?</h2>
          <p>
            Let's discuss your requirements and create a custom solution that drives your business forward.
          </p>
          <div className="cta-buttons">
            <Link to="/contact" className="btn-primary">Get Started</Link>
            <Link to="/portfolio" className="btn-secondary">View Our Work</Link>
          </div>
        </motion.div>
      </section>
    </div>
  );
};

export default Services;